import * as Sentry from '@sentry/browser';
import axios from 'axios';
import ajaxUrl from '../../shared/helpers/ajaxUrl';

declare global {
  interface Window {
    pageContext?: Record<string, unknown>;
    segmentContext?: Record<string, unknown>;
  }
}

export default class BannerService {
  static logSentry = 0;

  constructor() {
    const body = document.querySelector('body');
    if (!body.classList.contains('banner-service')) {
      BannerService.getBanner();
      body.classList.add('banner-service');
    }
  }

  public static getBanner() {
    const pageContext = window.pageContext || {};
    const segmentContext = window.segmentContext || {};
    const data = {
      action: 'banner',
      data: {
        action: 'banner',
        context: {
          pageContext,
          segmentContext,
        },
      },
    };
    axios({
      method: 'POST',
      url: ajaxUrl,
      data,
      responseType: 'json',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => {
        if (response.status === 200) {
          const dataResponse = response.data;
          BannerService.injectBanner(dataResponse);
        }
      })
      .catch((error) => {
        if (BannerService.logSentry) {
          Sentry.captureException(`[EMINZA-BANNER] Ajax error: ${error}`);
        } else {
          console.error(`[EMINZA-BANNER] error: ${error}`);
        }
      });
  }

  static injectBanner(bannerHtml: string) {
    const tempContainer = this.createTempContainer(bannerHtml);
    const emplacements = this.extractEmplacements(tempContainer);
    this.injectBannersIntoDom(tempContainer, emplacements);
  }

  // Créé un conteneur temporaire pour manipuler le HTML
  private static createTempContainer(bannerHtml: string): HTMLElement {
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = bannerHtml;
    return tempContainer;
  }

  // Extrait les emplacements à partir des inputs présents dans le HTML temporaire
  private static extractEmplacements(
    tempContainer: HTMLElement
  ): Record<
    string,
    Record<string, { selecteurCss: string; position: string }>
  > {
    const emplacements: Record<
      string,
      Record<string, { selecteurCss: string; position: string }>
    > = {};

    tempContainer
      .querySelectorAll('input[name^="emplacements"]')
      .forEach((input) => {
        const name = input.getAttribute('name');
        const value = input.getAttribute('value');

        if (name && value) {
          const matches = name.match(
            /emplacements\[(\d+)\]\[(\d+)\]\[([^\]]+)\]/
          );
          if (matches) {
            const [, bandeauId, emplacementId, attribute] = matches;

            // Initialiser le bandeau si nécessaire
            if (!emplacements[bandeauId]) {
              emplacements[bandeauId] = {};
            }

            // Initialiser l'emplacement avec des valeurs par défaut
            if (!emplacements[bandeauId][emplacementId]) {
              emplacements[bandeauId][emplacementId] = {
                selecteurCss: '',
                position: '',
              };
            }

            // Assigner dynamiquement la valeur de l'attribut
            (emplacements[bandeauId][emplacementId] as any)[attribute] = value;
          }
        }
      });

    return emplacements;
  }

  // Injecte les bandeaux dans les emplacements correspondants
  private static injectBannersIntoDom(
    tempContainer: HTMLElement,
    emplacements: Record<
      string,
      Record<string, { selecteurCss: string; position: string }>
    >
  ) {
    Object.entries(emplacements).forEach(([bandeauId, emplacementData]) => {
      Object.entries(emplacementData).forEach(([, emplacement]) => {
        const { selecteurCss, position } = emplacement;

        const targetElement = document.querySelector(selecteurCss);

        if (targetElement) {
          const bandeauxContents = tempContainer.querySelectorAll(
            `[data-bandeau-id="${bandeauId}"]`
          );

          bandeauxContents.forEach((bandeauContent) => {
            const isDesktop = bandeauContent.classList.contains(
              'banner-desktop__content'
            );
            const isMobile = bandeauContent.classList.contains(
              'banner-mobile__content'
            );

            if (isDesktop || isMobile) {
              this.insertContentIntoTarget(
                targetElement,
                bandeauContent,
                position
              );
            }
          });
        }
      });
    });
  }

  // Insère le contenu du bandeau à la position spécifiée
  private static insertContentIntoTarget(
    targetElement: Element,
    bandeauContent: Element,
    position: string
  ): void {
    const emptiedClass = 'content-emptied';
    if (position === 'before') {
      targetElement.insertAdjacentElement(
        'beforebegin',
        <Element>bandeauContent.cloneNode(true)
      );
    } else if (position === 'after') {
      targetElement.insertAdjacentElement(
        'afterend',
        <Element>bandeauContent.cloneNode(true)
      );
    } else if (position === 'prepend') {
      targetElement.prepend(bandeauContent.cloneNode(true));
    } else if (position === 'replace') {
      if (!targetElement.classList.contains(emptiedClass)) {
        targetElement.innerHTML = '';
        targetElement.classList.add(emptiedClass);
      }
      targetElement.append(bandeauContent.cloneNode(true));
    } else {
      // Default: append
      targetElement.append(bandeauContent.cloneNode(true));
    }
  }
}
